import $ from 'jquery';
import util from 'util/Util';
import BasePropertyView from 'BasePropertyView';
import formatTemplate from 'properties/main/numeric.ejs';
import numericUtil from 'shared/NumericUtil';
import constants        from 'Constants.js';

class MainNumericView extends BasePropertyView {

    initialize( opts ) {
        this.options = opts || {};
        this.template = formatTemplate;
        this.tagName = 'div';
        this.id = opts.el;
        this.config = opts.config || {};
        this.selectedColor = 'lightgrey';
        this.unselectedColor = 'transparent';
        this.events = {
            'change input[type=number], input[type=text], select' : 'updateAttributes',
            'blur input[type=text], input[type=number]'   : 'lostFocus',
            'focus input[type=text], input[type=number]'   : 'getFocus',
            'click img' : 'alignmentHandler',
            'change input[type=checkbox]' : 'updateCheckAttributes',
            'keydown input[type=text]' : 'validateInputKeyPressed'
        };
        if (!this.model.get('align')) {
            this.model.set('align', 'right', {silent:true});
        }

        if (this.model.get('paddingAmmount') && this.model.get('paddingAmmount') === '0') {
            this.model.set('paddingAmmount', '1', {silent:true});
        }

        // Realize the UI model "align" attribute as the UI alignment highlight model value
        this.clearAlignmentSelections();
        const alignmentHilight =
            { 'left': 'alignLeftSelection', 'center': 'alignCenterSelection', 'right': 'alignRightSelection'}[this.model.get('align')];
        this.model.set( alignmentHilight, this.selectedColor, {silent: true});

        this.listenTo(this.model, 'change', this.render);
        return this;
    }

    validateInputKeyPressed(ev) {
        const propName = ev.currentTarget.name;

        if (propName === 'text') { //Apply all validation rules to the input text for numeric value
            let allowNegative = util.isTrue(this.model.get('allowNegative'));

            if ($.inArray(ev.keyCode, [8, 9, 13, 27, 46]) !== -1 ||
                // Allow: Ctrl+C, Command+C, Ctrl+V, Command+V, Ctrl+X, Command+X
                ((ev.keyCode === 67 || ev.keyCode === 86 || ev.keyCode === 88) && (ev.ctrlKey === true || ev.metaKey === true)) ||
                // Allow: home, end, left, right, down, up
                (ev.keyCode >= 35 && ev.keyCode <= 40)) {
                return;
            }

            if ($.inArray(ev.keyCode, [109, 189]) !== -1) {
                //If negative is allowed then avoid introducing more than one sign
                if (!allowNegative || ev.currentTarget.value.indexOf('-') !== -1) {
                    ev.preventDefault();
                }
            } else if ($.inArray(ev.keyCode, [110, 190]) !== -1) {
                //If decimal point is allowed then avoid introducing more than one point
                if (ev.currentTarget.value.indexOf('.') !== -1) {
                    ev.preventDefault();
                }
            } else if ((ev.shiftKey || (ev.keyCode < 48 || ev.keyCode > 57)) && (ev.keyCode < 96 || ev.keyCode > 105)) {
                // Ensure that it is a number and stop the keypress
                ev.preventDefault();
            }
        }
    }
    

    updateAttributes(ev) {
        ev.preventDefault();
        const propName = ev.currentTarget.name;
        let propVal = ev.currentTarget.value;
        let isValid = true;

        if (propName === 'paddingAmmount' && parseInt(propVal) > 15) {
            propVal = 15;
        } else if (propName === 'paddingAmmount' && parseInt(propVal) < 1) {
            propVal = 1;
        } else if (propName === 'decimalLength') {
            const maxDecimalLength = util.extractNum(this.model.get('maxDecimalLength'));
            if (isNaN(propVal) || util.extractNum(propVal) <= 0) {
                propVal = 0;
            } else if (util.extractNum(propVal) > maxDecimalLength) {
                propVal = maxDecimalLength;
            }
        } else if (propName === 'decimalSeparator') { //Manage rules between decimalSeparator and thousandsSeparator on designer
            if (this.model.get('thousandsSeparator') === propVal) {
                isValid = false;

                let validVal = ',';
                if (propVal === ',') { //There are only two possible values for decimalSeparator
                    validVal = '.';
                }
                this.model.set(propName, validVal, { silent: true });
            }
        } else if (propName === 'thousandsSeparator') {
            if (this.model.get('decimalSeparator') === propVal) {
                isValid = false;
                this.model.set(propName, '', { silent: true });
            }
        } else if (propName === 'min' || propName === 'max') {
            const minValueAccepted = util.extractNum(constants.NUMERIC_INPUT_MIN_VALUE);
            const maxValueAccepted = util.extractNum(constants.NUMERIC_INPUT_MAX_VALUE);
            const min = util.extractNum(this.model.get('min'));
            const max = util.extractNum(this.model.get('max'));
            propVal = util.extractNum(propVal);

            //Check min-max against reference values
            if (propVal < minValueAccepted) {
                propVal = minValueAccepted;
            } else if (propVal > maxValueAccepted) {
                propVal = maxValueAccepted;
            }

            //Check if min-max are not overflowing each other
            if (propName === 'min' && propVal > max) {
                propVal = max;
            } else if (propName === 'max' && propVal < min) {
                propVal = min;
            }

            //If one of the min-max values is negative then we need to turn on the allowNegative feature
            if (propVal < 0 && !util.isTrue(this.model.get('allowNegative'))){
                this.model.set('allowNegative', true, { silent: true });
            }
        }

        if (isValid) {
            this.model.set(propName, propVal);
        }

        this.render();
    }

    updateCheckAttributes(ev) {
        ev.preventDefault();
        const propName = ev.currentTarget.name;
        const propVal  = ev.currentTarget.checked;

        if (propName === 'allowNegative') {
            if (!util.isTrue(propVal)){
                let propText = util.extractNum(this.model.get('text'));
                if (propText < 0){
                    propText = propText * -1;
                    this.model.set('text', propText, { silent: true });
                }
            }
        }

        this.model.set(propName, propVal);
        this.render();
    }

    clearAlignmentSelections() {
        ['alignLeftSelection', 'alignCenterSelection', 'alignRightSelection']
            .forEach( v => this.model.set(v, this.unselectedColor, {silent:true} )) ;
    }

    switchAlignment( propName ) {
        let newValue;

        this.clearAlignmentSelections();

        if (propName === "align-center") {
            newValue = "center";
            this.model.set('alignCenterSelection', this.selectedColor, {silent: true});
        } else if (propName === "align-right") {
            newValue = "right";
            this.model.set('alignRightSelection', this.selectedColor, {silent: true});
        }
        else if (propName === "align-left") {
            newValue = "left";
            this.model.set('alignLeftSelection', this.selectedColor, {silent: true});
        }
        this.model.set('align', newValue);
    }

    alignmentHandler(ev) {
        ev.preventDefault();
        this.switchAlignment( ev.currentTarget.name );
    }


    render() {
        this.$el.empty();

        numericUtil.transform(this.model);  

        if(this.template) {
            this.$el.html(this.template(this.model.toJSON()));
        }

        var thousandsSeparator = this.model.get('thousandsSeparator');
        var decimalSeparator = this.model.get('decimalSeparator');

        if (decimalSeparator) {
            $('#decimalSeparator').find("option[value='\\" + decimalSeparator + "']").attr('selected', true);
        }
        if (thousandsSeparator) {
            $('#thousandsSeparator').find("option[value='\\" + thousandsSeparator + "']").attr('selected', true);
        }
        
        if (this.model.get('paddingAlignment')) {
            $('#paddingAlignment').find("option[value='"+this.model.get('paddingAlignment')+"']").attr('selected', true);
        }

        let bAllowNegative = util.isTrue(this.model.get('allowNegative'));
        $(`input[name='allowNegative']`).prop('checked', bAllowNegative );

        let bShowLeadingZero = util.isTrue(this.model.get('showLeadingZero'));
        $(`input[name='showLeadingZero']`).prop('checked', bShowLeadingZero );

        let bPaddingEnabled = util.isTrue(this.model.get('paddingEnabled'));
        $(`input[name='paddingEnabled']`).prop('checked', bPaddingEnabled );

        if (this.config.showTitle) {
            this.$el.find('.propTitle').removeClass('hide');
        }
        
        this.delegateEvents();
        return this;
    }

}

export default MainNumericView;
